import React, { useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import {
  Chart as ChartJS,
  ChartEvent,
  ActiveElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import type { SummaryRow } from './analyticsTypes';
import { categoryColor } from './utils/chartPalette';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend);

interface EventDistributionChartProps {
  rows: SummaryRow[];
  /** When provided, bars become clickable; receives the event_name. */
  onBarClick?: (eventName: string) => void;
  /** Show counts as percentages of total instead of raw counts. */
  asPercent?: boolean;
  /** Cap displayed bars. Defaults to 20. */
  limit?: number;
}

const EventDistributionChart: React.FC<EventDistributionChartProps> = ({
  rows,
  onBarClick,
  asPercent = false,
  limit = 20,
}) => {
  const { t } = useTranslation('admin');
  const theme = useTheme();
  const sorted = useMemo(
    () => [...rows].sort((a, b) => b.count - a.count).slice(0, limit),
    [rows, limit],
  );
  const total = useMemo(() => rows.reduce((s, r) => s + r.count, 0), [rows]);

  const chartData = useMemo(
    () => ({
      labels: sorted.map((r) => r.event_name),
      datasets: [
        {
          label: asPercent ? t('analytics.charts.percentLabel') : t('analytics.charts.eventCountLabel'),
          data: sorted.map((r) => (asPercent && total > 0 ? (r.count / total) * 100 : r.count)),
          backgroundColor: sorted.map((r) => categoryColor(r.category, theme)),
          borderRadius: 3,
        },
      ],
    }),
    [sorted, theme, asPercent, total, t],
  );

  const options = useMemo(
    () => ({
      indexAxis: 'y' as const,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: { display: true, text: t('analytics.charts.topEventsTitle'), font: { size: 14 } },
        tooltip: {
          mode: 'index' as const,
          intersect: false,
          callbacks: asPercent
            ? {
                label: (ctx: { parsed: { x: number } }) => `${ctx.parsed.x.toFixed(1)}%`,
              }
            : undefined,
        },
      },
      scales: {
        x: { beginAtZero: true, ticks: asPercent ? { callback: (v: number | string) => `${v}%` } : undefined },
        y: { ticks: { font: { size: 11 } } },
      },
      onClick: onBarClick
        ? (_e: ChartEvent, elements: ActiveElement[]) => {
            if (elements.length === 0) return;
            const idx = elements[0].index;
            const row = sorted[idx];
            if (row) onBarClick(row.event_name);
          }
        : undefined,
      onHover: onBarClick
        ? (event: ChartEvent, elements: ActiveElement[]) => {
            const canvas = (event.native as MouseEvent | null)?.target as HTMLElement | null;
            if (canvas) canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
          }
        : undefined,
    }),
    [onBarClick, sorted, asPercent, t],
  );

  const height = Math.max(200, sorted.length * 28);

  const highestText = sorted.length > 0
    ? t('analytics.charts.topEventsAriaHighest', { name: sorted[0].event_name, count: sorted[0].count })
    : t('analytics.charts.topEventsAriaNoData');

  return (
    <Box sx={{ height }} role="img" aria-label={t('analytics.charts.topEventsAria', { count: sorted.length, highest: highestText })}>
      <Bar data={chartData} options={options} />
    </Box>
  );
};

export default React.memo(EventDistributionChart);
